import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureTransaction, ensureUser } from '../../util/data';
import { getUserTxRole } from '../../util/transactions';
import {
  ActivityFeed,
  IconSpinner,
  ReviewModal,
  UserDisplayName,
} from '../../components';
import { SendMessageForm } from '../../forms';

import css from './InboxPage.module.css';

// Functional component as a helper to build ActivityFeed section
const FeedSection = props => {
  const {
    className,
    rootClassName,
    currentUser,
    currentTransaction,
    fetchMessagesError,
    fetchMessagesInProgress,
    messages,
    oldestMessagePageFetched,
    onManageDisableScrolling,
    onSendMessage,
    onSendReview,
    onShowMoreMessages,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    type,
    intl,
  } = props;
  const [sendMessageFormFocused, setSendMessageFormFocused] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const onOpenReviewModal = () => setIsReviewModalOpen(true);
  const onCloseReviewModal = () => setIsReviewModalOpen(false);

  const onSubmitReview = (values) => {
    const ensuredTransaction = ensureTransaction(currentTransaction);
    const transactionRole = getUserTxRole(currentUser.id, ensuredTransaction);
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);
    onSendReview(transactionRole, ensuredTransaction, rating, reviewContent)
      .then(r => {
        setIsReviewModalOpen(false);
        setReviewSubmitted(true);
      })
      .catch(e => {
        // Do nothing.
      });
  };

  const onSendMessageFormFocus = () => setSendMessageFormFocused(true);

  const onSendMessageFormBlur = () => setSendMessageFormFocused(false);

  const onMessageSubmit = (values, form) => {
    const message = values.message ? values.message.trim() : null;
    const ensuredTransaction = ensureTransaction(currentTransaction);

    if (!message) {
      return;
    }
    onSendMessage(ensuredTransaction.id, message)
      .then(messageId => {
        form.reset();
        scrollToMessage(messageId);
      })
      .catch(e => {
        // Ignore, Redux handles the error
      });
  };

  const scrollToMessage = (messageId) => {
    const selector = `#msg-${messageId.uuid}`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  const isOrder = type === 'order';
  const { customer, provider } = currentTransaction;
  const otherUser = isOrder ? provider : customer;
  const otherUserDisplayName = <UserDisplayName user={otherUser} intl={intl} />;

  const txTransitions = currentTransaction.attributes.transitions
    ? currentTransaction.attributes.transitions
    : [];
  const hasOlderMessages = totalMessagePages > oldestMessagePageFetched;

  const showFeed = messages.length > 0 || txTransitions.length > 0 || fetchMessagesError;

  const currentProvider = ensureUser(currentTransaction.provider);
  const currentCustomer = ensureUser(currentTransaction.customer);

  const iscustomerLoaded = !!currentCustomer.id;
  const isCustomerBanned = iscustomerLoaded && currentCustomer.attributes.banned;
  const isCustomerDeleted = iscustomerLoaded && currentCustomer.attributes.deleted;
  const isProviderLoaded = !!currentProvider.id;
  const isProviderBanned = isProviderLoaded && currentProvider.attributes.banned;
  const isProviderDeleted = isProviderLoaded && currentProvider.attributes.deleted;

  const showSendMessageForm =
    !isCustomerBanned && !isCustomerDeleted && !isProviderBanned && !isProviderDeleted;

  const sendMessagePlaceholder = intl.formatMessage({ id: 'InboxPage.sendMessagePlaceholder' });
  const sendingMessageNotAllowed = intl.formatMessage({ id: 'InboxPage.sendingMessageNotAllowed' });

  const classes = classNames(rootClassName || css.activityFeedContainer, className);

  return showFeed ? (
    <div className={classes}>
      <div className={css.topFeedContainer}>
        <span className={css.feedUsername}>{otherUserDisplayName}</span>
      </div>
      {fetchMessagesError ? (
        <p className={css.messageError}>
          <FormattedMessage id='InboxPage.messageLoadingFailed' />
        </p>
      ) : null}
      {fetchMessagesInProgress ? (
        <IconSpinner className={css.Spinner} />
      ) : (
        <div className={css.feedContainer}>
          <div className={css.messagesAndActivityContainer}>
            <ActivityFeed
              className={css.activityFeed}
              messages={messages}
              transaction={currentTransaction}
              currentUser={currentUser}
              hasOlderMessages={hasOlderMessages && !fetchMessagesInProgress}
              onOpenReviewModal={onOpenReviewModal}
              onShowOlderMessages={onShowMoreMessages}
              fetchMessagesInProgress={fetchMessagesInProgress}
            />
          </div>
          {showSendMessageForm ? (
            <SendMessageForm
              formId='InboxPage.SendMessageForm'
              rootClassName={css.sendMessageForm}
              messagePlaceholder={sendMessagePlaceholder}
              inProgress={sendMessageInProgress}
              sendMessageError={sendMessageError}
              onFocus={onSendMessageFormFocus}
              onBlur={onSendMessageFormBlur}
              onSubmit={onMessageSubmit}
            />
          ) : (
            <div className={css.sendingMessageNotAllowed}>{sendingMessageNotAllowed}</div>
          )}
        </div>
      )}
      <ReviewModal
        id='ReviewOrderModal'
        isOpen={isReviewModalOpen}
        onCloseModal={onCloseReviewModal}
        onManageDisableScrolling={onManageDisableScrolling}
        onSubmitReview={onSubmitReview}
        revieweeName={otherUserDisplayName}
        reviewSent={reviewSubmitted}
        sendReviewInProgress={sendReviewInProgress}
        sendReviewError={sendReviewError}
      />
    </div>
  ) : null;
};

export default FeedSection;



// WEBPACK FOOTER //
// ./src/containers/InboxPage/FeedSection.js